// /apec/$province — TH APEC by-province landing (mirror of /en/apec/$province)
// Closes P31 hub gap: the TH side had only /apec, while EN already had per-province.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { APEC_PROVINCES, APEC_PROVINCE_SLUGS } from "@/data/apec-provinces";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/apec/$province")({
  beforeLoad: ({ params }) => {
    if (!APEC_PROVINCE_SLUGS.includes(params.province)) throw notFound();
  },
  head: ({ params }) => {
    const prov = APEC_PROVINCES.find((p) => p.slug === params.province)!;
    const thPath = `/apec/${prov.slug}`;
    const enPath = `/en/apec/${prov.slug}`;
    const title = `APEC Card (ABTC) สำหรับนักธุรกิจ${prov.nameTh} — รับยื่นครบวงจร | NYC`;
    const desc = `บริการขอ APEC Business Travel Card (ABTC) สำหรับผู้บริหารใน${prov.nameTh} เดินทาง 21 economies ฟรีวีซ่า — NYC รับเอกสารถึงที่ ${prov.nameTh} ยื่นกรมการกงสุล MFA กรุงเทพฯ ไม่ต้องเดินทาง. ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    const steps = [
      { name: "ตรวจคุณสมบัติฟรี", text: `ตรวจสถานะกรรมการ/ผู้ถือหุ้น/ผู้บริหารตาม Thai Nationality เฉพาะบริษัทใน${prov.nameTh}` },
      { name: "รับเอกสารถึงที่", text: `ทีม NYC รับเอกสารที่สำนักงานใน${prov.nameTh} หรือผ่าน EMS — เตรียม APEC-1, สำเนา passport, รูปถ่าย, หนังสือรับรองบริษัท, จดหมายอธิบายความจำเป็นทางธุรกิจ` },
      { name: "ยื่นกรมการกงสุล MFA กรุงเทพฯ", text: `ทีมยื่นที่กรมการกงสุล MFA แจ้งวัฒนะ — ลูกค้าใน${prov.nameTh} ไม่ต้องเดินทางมากรุงเทพฯ` },
      { name: "ติดตาม pre-clearance 21 economies", text: `21 ประเทศสมาชิก APEC ดำเนินการคู่ขนาน (3-4 เดือน) — รายงานสถานะรายเดือน` },
      { name: "ส่งบัตร ABTC ถึงบ้าน", text: `บัตรอายุ 5 ปี ส่ง EMS ฟรีถึงที่อยู่ใน${prov.nameTh} พร้อมคู่มือใช้ช่อง APEC Lane` },
    ];
    const faqs = [
      { q: `ลูกค้าใน${prov.nameTh}ต้องเดินทางมากรุงเทพฯ ไหม?`, a: `ไม่ต้อง — NYC รับเอกสารถึงที่ใน${prov.nameTh} และยื่น MFA กรุงเทพฯ ให้ทั้งหมด ลูกค้าเซ็นเอกสาร + ส่ง passport เท่านั้น` },
      { q: `APEC Card คุ้มกับผู้บริหารใน${prov.nameTh} ไหม?`, a: `${prov.localNote} เส้นทางหลักที่ใช้บัตร ABTC จาก${prov.nameTh}: ${prov.apecCorridor}` },
      { q: "ถือ ABTC แล้วยังต้องขอวีซ่าธุรกิจอีกไหม?", a: "ไม่ต้อง — ABTC ทำหน้าที่เป็น pre-cleared business visa ใน 19 จาก 21 economies APEC ตลอดอายุบัตร 5 ปี ครอบคลุมประชุม เจรจา เซ็นสัญญา และดูงาน ยกเว้นสหรัฐฯ และแคนาดาที่ให้สิทธิ์เฉพาะใช้ APEC Lane (ต้องมีวีซ่าธุรกิจแยก) ระยะพำนักต่อครั้งทั่วไป 60-90 วัน" },
      { q: "ระยะเวลารวมเท่าไร?", a: "ระยะเวลาดำเนินการรวมประมาณ 4-6 เดือนนับจากวันยื่นคำขอ ABTC ที่กรมการกงสุล MFA โดยแบ่งเป็น: (1) MFA ออกบัตรเบื้องต้น 4-6 สัปดาห์ (2) ส่งข้อมูลให้ทุก economy ทำ pre-clearance พร้อมกัน 3-5 เดือน ขึ้นกับประเทศที่ช้าสุด (โดยทั่วไปจีนและเปรูใช้เวลานานสุด ~5 เดือน, ญี่ปุ่นและสิงคโปร์เร็วสุด ~6 สัปดาห์) เมื่อครบทุก economy แล้ว MFA จะออกบัตรอายุ 5 ปี" },
      { q: `ค่าบริการนอกกรุงเทพฯ แพงกว่าไหม?`, a: `เท่ากัน — เหมา 12,000 บาท สำหรับลูกค้า${prov.nameTh} พร้อมค่า MFA 5,000 บาท รวมค่าส่ง EMS แล้ว` },
    ];
    const breadcrumbs = [
      { name: "หน้าแรก", path: "/" },
      { name: "APEC Card", path: "/apec" },
      { name: prov.nameTh, path: thPath },
    ];
    const localBusiness = {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LegalService",
        "@id": `${SITE}${thPath}#local`,
        name: `NYC Legal & Notary Service — รับยื่น APEC Card (${prov.nameTh})`,
        url: `${SITE}${thPath}`,
        telephone: "+66922429293",
        priceRange: "฿฿",
        areaServed: { "@type": "AdministrativeArea", name: prov.nameTh },
        address: { "@type": "PostalAddress", addressCountry: "TH", addressRegion: prov.region, addressLocality: prov.nameTh },
        geo: { "@type": "GeoCoordinates", latitude: prov.lat, longitude: prov.lng },
        geoRadius: "150000",
        knowsLanguage: ["th", "en"],
        makesOffer: { "@type": "Offer", name: "APEC Business Travel Card", priceCurrency: "THB", price: "12000" },
      }),
    };
    return {
      meta: [
        { title: ctaTitle(title, "/apec/$province", "th") },
        { name: "description", content: ctaDesc(desc, "/apec/$province", "th")},
        { name: "keywords", content: `APEC Card ${prov.nameTh}, ABTC ${prov.nameTh}, ขอ APEC Card ${prov.nameTh}, นักธุรกิจ ${prov.nameTh}` },
        { property: "og:title", content: ctaTitle(title, "/apec/$province", "th")},
        { property: "og:description", content: ctaDesc(desc, "/apec/$province", "th")},
        { property: "og:url", content: `${SITE}${thPath}` },
        { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        ...buildLandingSchemas({
          path: thPath, title, description: desc,
          serviceType: "APEC Business Travel Card Service",
          priceFromThb: 12000, steps, faqs, breadcrumbs,
        }),
        localBusiness,
      ],
    };
  },
  component: ApecProvinceTh,
});

function ApecProvinceTh() {
  const { province } = Route.useParams();
  const prov = APEC_PROVINCES.find((p) => p.slug === province)!;
  const desc = `ขอ APEC Business Travel Card สำหรับผู้บริหารใน${prov.nameTh} — NYC ยื่น MFA, ติดตาม pre-clearance 21 ประเทศ, ส่งบัตรถึงบ้าน ไม่ต้องเดินทางมากรุงเทพฯ — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
  return (
    <ServiceLanding
      badge={`APEC Card · ${prov.nameTh}`}
      title={`APEC Business Travel Card สำหรับผู้บริหาร${prov.nameTh}`}
      subtitle={`${prov.localNote} เส้นทาง APEC ที่ใช้บ่อย: ${prov.apecCorridor} · สนามบินใกล้สุด: ${prov.nearbyAirport}`}
      quickAnswer={{
        q: `ผู้บริหารใน${prov.nameTh}ขอ APEC Card อย่างไร?`,
        a: `NYC รับเอกสารถึงที่ ${prov.nameTh} ยื่นกรมการกงสุล MFA กรุงเทพฯ ให้ทั้งหมด — ลูกค้าไม่ต้องเดินทาง บัตรอายุ 5 ปี ใช้ได้ 21 economies ค่าบริการ 12,000 บาท + ค่า MFA 5,000 บาท ใช้เวลา 4-6 เดือน`,
      }}
      keyFacts={[
        { k: "พื้นที่บริการ", v: prov.nameTh },
        { k: "สนามบินใกล้สุด", v: prov.nearbyAirport },
        { k: "อุตสาหกรรมหลัก", v: prov.industryFocus },
        { k: "ค่าบริการ NYC", v: "12,000 บาท" },
      ]}
      heroBullets={[
        `เดินทาง 21 economies APEC ฟรีวีซ่า จาก ${prov.nameTh}`,
        "บัตรอายุ 5 ปี · ไม่จำกัดจำนวนครั้ง · ใช้ช่อง APEC Lane",
        `รับเอกสารถึงที่ ${prov.nameTh} — ยื่น MFA กรุงเทพฯ ให้`,
        `เหมาะกับ ${prov.industryFocus}`,
      ]}
      whoFor={{
        th: [`ผู้บริหาร/กรรมการบริษัทใน${prov.nameTh} ที่เดินทาง APEC บ่อย`, `ผู้ส่งออก-นำเข้าใน${prov.nameTh} ไปเอเชียแปซิฟิก`, `Startup founders ใน${prov.nameTh} ที่ต้อง pitch ใน SG/HK/TW`],
        foreign: [`Foreign directors of ${prov.nameTh}-registered companies eligible under Thai-nationality rules`, "Dual-nationals using a Thai passport for APEC business travel"],
      }}
      steps={steps_TH(prov.nameTh)}
      pricing={[
        { label: "ค่าธรรมเนียมราชการ (MFA)", price: "5,000", note: "อายุบัตร 5 ปี" },
        { label: "ค่าบริการ NYC — Standard", price: "12,000", note: `รวมรับเอกสารจาก ${prov.nameTh}` },
        { label: "Premium (เร่งด่วน + เลขาฯ)", price: "18,000", note: "ผู้ประสานงานส่วนตัว" },
      ]}
      faqs={[
        { q: `ลูกค้าใน${prov.nameTh}ต้องเดินทางมากรุงเทพฯ ไหม?`, a: `ไม่ต้อง — NYC รับเอกสารถึงที่ใน${prov.nameTh} และยื่น MFA กรุงเทพฯ ให้ทั้งหมด` },
        { q: `APEC Card คุ้มกับผู้บริหารใน${prov.nameTh} ไหม?`, a: `${prov.localNote} เส้นทางหลัก: ${prov.apecCorridor}` },
        { q: "ถือ ABTC แล้วยังต้องขอวีซ่าธุรกิจอีกไหม?", a: "ไม่ต้องขอวีซ่าซ้ำ ABTC ใช้แทน pre-cleared business visa ใน 19 จาก 21 economies APEC ตลอดอายุ 5 ปี สำหรับประชุม เจรจา ดูงาน เซ็นสัญญา ยกเว้นสหรัฐฯ และแคนาดาที่ให้สิทธิ์เฉพาะ APEC Lane ต้องมีวีซ่าธุรกิจของประเทศนั้นแยก ระยะพำนักทั่วไป 60-90 วัน" },
        { q: "ระยะเวลารวมเท่าไร?", a: "ระยะเวลาดำเนินการรวมประมาณ 4-6 เดือนนับจากวันยื่นคำขอที่กรมการกงสุล MFA แบ่งเป็นการออกบัตรเบื้องต้น 4-6 สัปดาห์ และ pre-clearance ของ 19 economies พร้อมกัน 3-5 เดือน (จีนและเปรูใช้เวลานานสุด ญี่ปุ่นและสิงคโปร์เร็วสุด) เมื่อครบทุก economy MFA จะออกบัตรอายุ 5 ปี ใช้เดินทางได้ไม่จำกัดจำนวนครั้ง" },
        { q: "ค่าบริการนอกกรุงเทพฯ แพงกว่าไหม?", a: `เท่ากัน — เหมา 12,000 บาท สำหรับลูกค้า${prov.nameTh} + MFA 5,000 บาท รวมค่าส่ง EMS` },
      ]}
      contactNote={desc}
      relatedLinks={[
        { to: "/apec", label: "APEC Card — ภาพรวม" },
        { to: `/en/apec/${prov.slug}`, label: `APEC Card ${prov.name} (EN)` },
        { to: "/visa", label: "วีซ่าทุกประเทศ" },
        { to: "/translate/mfa", label: "แปล + รับรอง MFA" },
        { to: "/police-clearance", label: "Police Clearance" },
        { to: "/blog/apec-business-travel-card-21-economies", label: "บทความ: APEC Card คืออะไร" },
      ]}
    />
  );
}

function steps_TH(province: string) {
  return [
    { name: "ตรวจคุณสมบัติฟรี", text: `ตรวจสถานะกรรมการ/ผู้ถือหุ้น/ผู้บริหารตาม Thai Nationality เฉพาะบริษัทใน${province}` },
    { name: "รับเอกสารถึงที่", text: `ทีม NYC รับเอกสารที่สำนักงานใน${province} หรือ EMS` },
    { name: "ยื่น MFA กรุงเทพฯ", text: `ยื่นที่กรมการกงสุล MFA แจ้งวัฒนะ — ไม่ต้องเดินทาง` },
    { name: "ติดตาม pre-clearance", text: `21 ประเทศคู่ขนาน 3-4 เดือน — รายงานรายเดือน` },
    { name: "ส่งบัตรถึงบ้าน", text: `บัตรอายุ 5 ปี EMS ถึง${province}` },
  ];
}
